import {
  endOfDay,
  endOfWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subWeeks,
  subMonths,
  subQuarters,
  subYears,
  addDays,
  addWeeks,
  addMonths,
  addQuarters,
  addYears,
} from 'date-fns';
import { PeriodGroupByEnum } from 'graphql-common';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateFormatForApi } from '@lib/enums/dateTime';
import { TREND_QUARTER, TrendGroupBy } from './getTrendGroupByOptions';

// Monday-start week — used consistently for ISO week alignment.
export const WEEK_OPTS = { weekStartsOn: 1 as const };

/** Returns the start of the bucket that contains `date` for the given granularity. */
export function getBucketStart(date: Date, groupBy: TrendGroupBy): Date {
  switch (groupBy) {
    case PeriodGroupByEnum.Year:
      return startOfYear(date);
    case TREND_QUARTER:
      return startOfQuarter(date);
    case PeriodGroupByEnum.Month:
      return startOfMonth(date);
    case PeriodGroupByEnum.Week:
      return startOfWeek(date, WEEK_OPTS);
    case PeriodGroupByEnum.Day:
    default:
      return startOfDay(date);
  }
}

/** Returns the end of the bucket that contains `date` for the given granularity. */
export function getBucketEnd(date: Date, groupBy: TrendGroupBy): Date {
  switch (groupBy) {
    case PeriodGroupByEnum.Year:
      return endOfYear(date);
    case TREND_QUARTER:
      return endOfQuarter(date);
    case PeriodGroupByEnum.Month:
      return endOfMonth(date);
    case PeriodGroupByEnum.Week:
      return endOfWeek(date, WEEK_OPTS);
    case PeriodGroupByEnum.Day:
    default:
      return endOfDay(date);
  }
}

/**
 * Steps `date` forward (+) or backward (–) by `amount` buckets of the given
 * granularity. Pass a negative `amount` to step backwards.
 */
export function getBucketStep(
  date: Date,
  groupBy: TrendGroupBy,
  amount: number,
): Date {
  switch (groupBy) {
    case PeriodGroupByEnum.Year:
      return amount >= 0 ? addYears(date, amount) : subYears(date, -amount);
    case TREND_QUARTER:
      return amount >= 0
        ? addQuarters(date, amount)
        : subQuarters(date, -amount);
    case PeriodGroupByEnum.Month:
      return amount >= 0 ? addMonths(date, amount) : subMonths(date, -amount);
    case PeriodGroupByEnum.Week:
      return amount >= 0 ? addWeeks(date, amount) : subWeeks(date, -amount);
    case PeriodGroupByEnum.Day:
    default:
      return amount >= 0 ? addDays(date, amount) : subDays(date, -amount);
  }
}

export interface TrendViewport {
  /** API-ready start date string (yyyy-MM-dd) */
  startDate: string;
  /** API-ready end date string (yyyy-MM-dd) */
  endDate: string;
  startObj: Date;
  endObj: Date;
}

/**
 * Computes the chart viewport (the date range fetched from the API and
 * rendered on the x-axis) based on the anchor date and grouping granularity.
 *
 * Viewport rules per mode:
 * - Day    → full calendar month of `endDateInput`
 * - Week   → weeks overlapping the calendar month of `endDateInput`
 * - Month  → Jan–Dec of the year of `endDateInput` (caller must pass endOfYear)
 * - Quarter→ Q1–Q4 of the year of `endDateInput` (caller must pass endOfYear)
 * - Year   → `periodStartOverride` year → `endDateInput` year (full period range)
 */
export default function getTrendPeriod(
  endDateInput: Date | undefined,
  groupBy: TrendGroupBy,
  /** For Year mode: extends the viewport back to the start of the period range. */
  periodStartOverride?: Date,
): TrendViewport {
  const baseEnd = endDateInput || new Date();
  let startObj: Date;
  let endObj: Date;

  switch (groupBy) {
    case PeriodGroupByEnum.Day:
      startObj = startOfMonth(baseEnd);
      endObj = endOfMonth(baseEnd);
      break;

    case PeriodGroupByEnum.Week:
      // Extend slightly past the month boundary so weeks that straddle the
      // first or last day of the month are fully included.
      startObj = startOfWeek(startOfMonth(baseEnd), WEEK_OPTS);
      endObj = endOfWeek(endOfMonth(baseEnd), WEEK_OPTS);
      break;

    case PeriodGroupByEnum.Year:
      startObj = periodStartOverride
        ? startOfYear(periodStartOverride)
        : startOfYear(baseEnd);
      endObj = endOfYear(baseEnd);
      break;

    default: {
      // Month (12 buckets) and Quarter (4 buckets) always render the full
      // calendar year. The caller pre-anchors endDateInput to endOfYear so
      // stepping back N–1 buckets always lands on Jan 1 / Q1.
      const bucketCount = groupBy === PeriodGroupByEnum.Month ? 12 : 4;
      const bucketEnd = getBucketEnd(baseEnd, groupBy);
      const bucketStartBase = getBucketStep(
        bucketEnd,
        groupBy,
        -(bucketCount - 1),
      );
      startObj = getBucketStart(bucketStartBase, groupBy);
      endObj = bucketEnd;
      break;
    }
  }

  return {
    startDate: getFormattedDate(startObj, dateFormatForApi) || '',
    endDate: getFormattedDate(endObj, dateFormatForApi) || '',
    startObj,
    endObj,
  };
}
